import i18next from 'i18next';
import Types from '../../backbone/pages/workouts/util/Types';
import formatNumber from '../../utils/NumberFormatter';
import NumberFormats from '../../backbone/utils/personalizer/NumberFormats';
import Personalizer from 'personalizer';
import Conversion from '../../backbone/utils/Conversion';
import DistanceUnits from '../../backbone/utils/personalizer/DistanceUnits';
import NumberParser from '../../backbone/utils/parsers/NumberParser';
import DurationTimeValidator from './validator/DurationTimeValidator';
import NumberValidator from './validator/NumberValidator';
import { powerZonesMax } from '../../utils/powerZones/powerZonesUtil';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatNumberInputSeparator } from './WorkoutUtil';
import ConversionFactors from 'utils/personalizer/ConversionFactors';
// make sure namespace(s) used in this file are loaded for translations
i18next.loadNamespaces(['units', 'workout', 'validation', 'activity']);

const POWER_ZONE_COUNT = {
    running: 5,
    cycling: 7,
    default: 7
};

export const POWER_UNIT = {
    WATTS: 'watts',
    PERCENT_FTP: 'percent'
};

export const POSSIBLE_POWER_CURVE_DURATIONS = [5, 10, 20, 30, 60, 120, 300, 600, 1200, 1800, 3600];

export const getZoneBySportType = (allSportZone, sportType) => {
    return allSportZone[sportType.toUpperCase()] ?? allSportZone['DEFAULT'];
};

export const getCyclingPowerZoneUnitOptions = () => {
    return [
        {
            value: POWER_UNIT.WATTS,
            textKey: i18next.t('units:label_power_unit')
        },
        {
            value: POWER_UNIT.PERCENT_FTP,
            textKey: i18next.t('workout:power_zone_percent_FTP')
        }
    ];
};

export const getDurationText = (duration) => {
    return duration < 60
        ? i18next.t('activity:avg_power_range_customsec', { 0: duration })
        : duration < 3600
        ? Math.round(duration / 60) + ' ' + i18next.t('units:activity_duration_unit')
        : i18next.t('common:time_hour', { 0: Math.round(duration / 3600) });
};

export const getPowerCurveIntervalOptions = (powerCurveWorkoutTargetData) => {
    return powerCurveWorkoutTargetData['entries'].map((entry) => {
        const wattsText = entry.power ? entry.power + ' W' : i18next.t('workout:no_data_power_curve');
        const durationText = getDurationText(entry.duration);

        return {
            value: entry.duration,
            textKey: durationText + ' (' + wattsText + ')'
        };
    });
};

export const getZoneGroupOptions = (allSportZone, isPowerZone, sportType) => {
    let list = [];

    const zone = getZoneBySportType(allSportZone, sportType);

    if (zone) {
        const count = isPowerZone ? getPowerZoneCount(sportType) + 1 : getHeartRateZoneCount(zone);
        for (let i = 1; i < count; i += 1) {
            let additionalText = getLabelForTargetRange(i, zone, isPowerZone);

            list.push({
                value: i,
                textKey: i18next.t(
                    isPowerZone
                        ? 'workout:power_zone_with_range_values'
                        : 'workout:workout.step.target.type.heart.zone.list.no.zone.value',
                    { 0: i, 1: additionalText }
                )
            });
        }
    }

    return list;
};

/**
 * Description: Text based intensity targets for pool swim workouts
 * @returns {Array<object>}
 */
export const getEffortBasedOptions = () => {
    return [
        {
            value: 1,
            textKey: i18next.t('activity:interval_type_recovery')
        },
        {
            value: 3,
            textKey: i18next.t('workout:workout.step.target.heart.zone.name.2')
        },
        {
            value: 4,
            textKey: i18next.t('activity:moderate')
        },
        {
            value: 5,
            textKey: i18next.t('activity:hard')
        },
        {
            value: 6,
            textKey: i18next.t('activity:very_hard')
        },
        {
            value: 7,
            textKey: i18next.t('workout:all_out')
        },
        {
            value: 9,
            textKey: i18next.t('workout:ascending')
        },
        {
            value: 10,
            textKey: i18next.t('workout:descending')
        }
    ];
};

export const getCSSBasedValues = (cssValue, latestCSSFailed) => {
    const values = [];
    cssValue = cssValue || 833; // default to 2:00 css if none is available
    for (let i = 60; i >= -60; i--) {
        const indexString = i > 0 ? `+${i}` : `${i}`;
        const cssInSeconds = Personalizer.convertCriticalSwimSpeed(cssValue) + i;
        if (cssInSeconds > 0) {
            const cssString = latestCSSFailed ? '' : `(${Personalizer.personalizeDuration(cssInSeconds)})`;
            values.push({
                value: i,
                textKey: `${indexString} ${i18next.t('workout:sec_duration_workout')} ${cssString}`
            });
        }
    }
    return values;
};

export const getPowerZoneCount = (sportType) => {
    return sportType in POWER_ZONE_COUNT ? POWER_ZONE_COUNT[sportType] : POWER_ZONE_COUNT.default;
};

const getHeartRateZoneCount = (zone) => {
    let count = 1;

    Object.keys(zone).forEach(function(key) {
        if (key.indexOf('zone') > -1) {
            count += 1;
        }
    });

    return count;
};

const getLabelForTargetRange = (index, object, isPowerZone) => {
    let additionalText = i18next.t('workout:workout.step.target.heart.zone.name.' + index);

    if (index && object) {
        let floorValue = object['zone' + index + 'Floor'];
        let nextIndex = index + 1;
        let nextFloorValue = object['zone' + nextIndex + 'Floor'];
        let unitLabel = isPowerZone ? i18next.t('units:max_avg_power_unit') : i18next.t('units:heart_rate_unit');

        if (!isNaN(floorValue) && nextFloorValue && index < getPowerZoneCount(object)) {
            additionalText = floorValue + '-' + nextFloorValue + ' ' + unitLabel;
        } else if (!isNaN(floorValue) && object.maxHeartRateUsed) {
            additionalText = floorValue + '-' + object.maxHeartRateUsed + ' ' + unitLabel;
        } else if (!isNaN(floorValue)) {
            //mostly for power zone because that does not have a maxHeartRateUsed like values
            let value = floorValue + ' ' + unitLabel;
            additionalText = i18next.t('workout:over_value', { 0: value });
        }
    }

    return additionalText;
};

/**
 * Description: Get the fields to display for the given target
 * Backbone Reference: getZonesValues() in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {*} selectedZone
 * @param {*} availableZones
 * @returns {{targetValueTwo: *, targetValueOne: string}}
 */
export const getZonesValues = (selectedZone, availableZones) => {
    let obj = {};
    if (availableZones && typeof availableZones === 'object' && !isNaN(selectedZone) && selectedZone > 0) {
        let zoneValue = parseInt(selectedZone);
        if (availableZones[`zone${zoneValue + 1}Floor`]) {
            //if we have an aditional zone to get the max value
            obj.targetValueOne = availableZones[`zone${zoneValue}Floor`];
            obj.targetValueTwo = availableZones[`zone${zoneValue + 1}Floor`];
        } else if (availableZones[`zone${zoneValue}Floor`] && availableZones.maxHeartRateUsed) {
            //if there is no next zone (is last zone) - and is hearth rate
            obj.targetValueOne = availableZones[`zone${zoneValue}Floor`];
            obj.targetValueTwo = availableZones.maxHeartRateUsed;
        } else {
            obj.targetValueOne = availableZones[`zone${zoneValue}Floor`];
            obj.targetValueTwo = null;
        }
    }

    return obj;
};

export const getTargetZoneFieldString = (targetValueOne, targetValueTwo, zoneNumber, isPowerZone) => {
    const unit = isPowerZone ? i18next.t('units:max_avg_power_unit') : i18next.t('units:heart_rate_unit');
    const zone = isPowerZone
        ? i18next.t('workout:workout.step.target.type.power.zone.list.no.zone.value', { 0: zoneNumber })
        : i18next.t('workout:heart_rate_zone_value', { 0: zoneNumber });
    let retVal;
    if (!isNaN(targetValueOne) && !isNaN(targetValueTwo) && targetValueOne !== null && targetValueTwo !== null) {
        retVal = zone + '</br>' + '(' + targetValueOne + '-' + targetValueTwo + ' ' + unit + ')';
    } else if (!isNaN(targetValueOne) && targetValueOne !== null) {
        retVal = zone + '</br>' + '(' + i18next.t('workout:over') + '-' + targetValueOne + ' ' + unit + ')';
    }

    return retVal;
};

/**
 * Description: Get the default value for selected target type
 * Backbone Reference: updateUI() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @param {{ targetType: string, sportTypeKey: string, isSecondaryTarget: boolean, powerZone: object, durationTargetTime: number, isFTP: boolean }}
 * @return {{
 *     targetType, targetValueOne, targetValueTwo, zoneNumber, powerCurveDuration, powerCurveScale
 * }}
 */
export const getDefaultTargetValue = ({
    targetType,
    sportTypeKey,
    isSecondaryTarget,
    powerZone,
    durationTargetTime,
    isFTP
}) => {
    let targetValueOne, targetValueTwo, zoneNumber, powerCurveDuration, powerCurveScale;
    const isRunning = sportTypeKey === Types.SportTypes.Running;
    switch (targetType) {
        case Types.TargetTypes.Cadence:
            targetValueOne = isRunning ? 175 : 80;
            targetValueTwo = isRunning ? 185 : 90;
            break;
        case Types.ChildTargetTypes.Select_Heart_Rate_Zone:
            zoneNumber = isRunning ? 3 : 1;
            break;
        case Types.ChildTargetTypes.Select_Power_Zone:
            zoneNumber = 1;
            break;
        case Types.TargetTypes.Heart_Rate_Zone:
            targetValueOne = 120;
            targetValueTwo = 130;
            break;
        case Types.TargetTypes.Pace_Zone:
            // values are in seconds (ex: 120 = 2:00)
            const lowerPaceValue = sportTypeKey === Types.SportTypes.Swimming ? 120 : isMetric() ? 330 : 510;
            const upperPaceValue =
                sportTypeKey === Types.SportTypes.Swimming
                    ? null // pool swim workout pace doesn't have an upper value, API/device expects null
                    : isMetric()
                    ? 360
                    : 570;
            targetValueOne = customPersonalizePace(
                Personalizer.convertPaceToMetersPerSecond(lowerPaceValue),
                isSecondaryTarget
            );
            targetValueTwo =
                sportTypeKey === Types.SportTypes.Swimming
                    ? null // pool swim workout pace doesn't have an upper value, API/device expects null
                    : customPersonalizePace(
                          Personalizer.convertPaceToMetersPerSecond(upperPaceValue),
                          isSecondaryTarget
                      );
            break;
        case Types.TargetTypes.Power_Zone:
            if (isFTP) {
                targetValueOne = 95;
                targetValueTwo = 105;
            } else if (powerZone && !isNaN(powerZone.functionalThresholdPower)) {
                if (isRunning) {
                    targetValueOne = 200;
                    targetValueTwo = 400;
                } else {
                    targetValueOne = powerZone.functionalThresholdPower - 10;
                    targetValueTwo = powerZone.functionalThresholdPower + 10;
                }
            } else {
                targetValueOne = 0;
                targetValueTwo = 0;
            }
            break;
        case Types.TargetTypes.Speed_Zone:
            let lowerSpeedValue = isMetric() ? 22 : 14;
            let upperSpeedValue = isMetric() ? 25 : 16;
            targetValueOne = Personalizer.personalizeSpeed(
                Personalizer.convertSpeedToMetersPerSecond(lowerSpeedValue),
                'cycling',
                null
            );
            targetValueTwo = Personalizer.personalizeSpeed(
                Personalizer.convertSpeedToMetersPerSecond(upperSpeedValue),
                'cycling',
                null
            );
            break;
        case Types.TargetTypes.Power_Curve:
            powerCurveScale = 90;
            powerCurveDuration = durationTargetTime ? closestDurationValue(durationTargetTime) : 1200;
            break;
        case Types.TargetTypes.No_Target:
            targetValueOne = null;
            targetValueTwo = null;
            zoneNumber = null;
            break;
        case Types.TargetTypes.Effort_Based:
            targetValueOne = 1; // default is Recovery
            targetValueTwo = 0; // valueTwo is not used for effort based intensity target
            break;
        case Types.TargetTypes.CSS_Based_Target_Pace:
            targetValueOne = 0; // default +0 for css pace
            targetValueTwo = 0; // valueTwo is not used for this target type
    }

    return { targetType, targetValueOne, targetValueTwo, zoneNumber, powerCurveDuration, powerCurveScale };
};

/**
 * Description: format the target value for save
 * Backbone Reference: getDataForSave() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @param {string} targetType
 * @param {*} value
 * @param {*} powerZone
 * @param {*} heartRateZone
 * @param {string} powerUnit
 * @param {*} isSecondaryTarget
 * @param {string} sportType
 * @param {string} poolLengthUnit
 * @returns {{secondaryTargetType: *, secondaryTargetValueOne: *, secondaryZoneNumber: *, secondaryTargetValueTwo: *, secondaryTargetPowerCurveDuration: *, secondaryPowerCurveScal: *}|
 * {targetValueTwo: *, zoneNumber: *, targetType: *, targetValueOne: *, powerCurveDuration: *, powerCurveScal: *}}
 */
export const formatDataForSave = (
    targetType,
    value,
    powerZone,
    heartRateZone,
    powerUnit,
    isSecondaryTarget,
    sportType,
    poolLengthUnit
) => {
    let targetValueOne = value.targetValueOne;
    let targetValueTwo = value.targetValueTwo;
    let zoneNumber = value.zoneNumber;
    let selectedTargetType = targetType;
    let powerUnitIsFtpPercent = powerUnit === POWER_UNIT.PERCENT_FTP;

    const numberParser = new NumberParser({
        numberSeparator: Personalizer.getNumberSeparator()
    });

    switch (targetType) {
        case Types.ChildTargetTypes.Select_Heart_Rate_Zone:
            const heartRateZonesValues = getZonesValues(value.zone, heartRateZone);
            targetValueOne = heartRateZonesValues.targetValueOne;
            targetValueTwo = heartRateZonesValues.targetValueTwo;
            selectedTargetType = Types.TargetTypes.Heart_Rate_Zone; //change to heart rate zone type
            break;
        case Types.TargetTypes.Pace_Zone:
            targetValueOne = convertPaceToSpeed(value.targetValueOne);

            // pool swim is always per 100m, convert value to meters if pool size is in yards
            if (sportType === Types.SportTypes.Swimming) {
                const [mm, ss] = value.targetValueOne.split(':');
                const totalSeconds = +ss + +mm * 60;
                // if pool size is in yards convert the value to meters, device always expects meters and API doesn't do a conversion on this field
                const paceDistance = poolLengthUnit === 'yard' ? 100 * ConversionFactors.YardsToMeters : 100;
                targetValueOne = paceDistance / totalSeconds;
            }

            // pool swim workout pace doesn't have an upper value, API/device expects null
            targetValueTwo = sportType === Types.SportTypes.Swimming ? null : convertPaceToSpeed(value.targetValueTwo);
            break;
        case Types.ChildTargetTypes.Select_Power_Zone:
            const powerZonesValues = getZonesValues(value.zone, powerZone);
            targetValueOne = powerZonesValues.targetValueOne;
            targetValueTwo = powerZonesValues.targetValueTwo;
            selectedTargetType = Types.TargetTypes.Power_Zone; //change to power zone type
            break;
        case Types.TargetTypes.Speed_Zone:
            const unit = isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile;
            const valueOne = numberParser.parse(String(value.targetValueOne) || '0');
            const valueTwo = numberParser.parse(String(value.targetValueTwo) || '0');
            targetValueOne = Conversion.convertSpeedToMPS(unit, valueOne);
            targetValueTwo = Conversion.convertSpeedToMPS(unit, valueTwo);
            break;
    }

    const retVal = getTargetObject(
        targetValueOne,
        targetValueTwo,
        zoneNumber,
        value.powerCurveDuration,
        value.powerCurveScale,
        selectedTargetType,
        isSecondaryTarget
    );

    retVal[isSecondaryTarget ? 'secondaryTargetValueUnit' : 'targetValueUnit'] = powerUnitIsFtpPercent
        ? {
              unitKey: 'percent'
          }
        : null;

    return retVal;
};

/**
 * Description: format the target value for edite
 * Backbone Reference: updateUI() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @param {*} workoutStepData
 * @param {*} isSecondaryTarget
 * @param {string} sportTypeKey
 * @param {string} poolLengthUnit
 * @returns {{targetValueTwo: *, zoneNumber: *, targetType: *, targetValueOne: *, powerCurveDuration: *, powerCurveScale: *}}
 */
export const formatDataForEdit = (workoutStepData, isSecondaryTarget, sportTypeKey, poolLengthUnit) => {
    const targetTypeLabel = isSecondaryTarget ? 'secondaryTargetType' : 'targetType';
    const zoneNumberLabel = isSecondaryTarget ? 'secondaryZoneNumber' : 'zoneNumber';
    const firstTarget = isSecondaryTarget ? 'secondaryTargetValueOne' : 'targetValueOne';
    const secondTarget = isSecondaryTarget ? 'secondaryTargetValueTwo' : 'targetValueTwo';
    const powerUnitLabel = isSecondaryTarget ? 'secondaryTargetValueUnit' : 'targetValueUnit';
    const powerCurveDurationLabel = isSecondaryTarget ? 'secondaryTargetPowerCurveDuration' : 'powerCurveDuration';
    const powerCurveScaleLabel = isSecondaryTarget ? 'secondaryTargetPowerCurveScale' : 'powerCurveScale';
    const targetType = workoutStepData[targetTypeLabel]?.workoutTargetTypeKey;
    const zoneNumber = workoutStepData[zoneNumberLabel];
    const powerUnit = workoutStepData[powerUnitLabel]?.unitKey;
    const powerCurveDuration = workoutStepData[powerCurveDurationLabel];
    const powerCurveScale = workoutStepData[powerCurveScaleLabel];
    let targetValueOne = workoutStepData[firstTarget];
    let targetValueTwo = workoutStepData[secondTarget];

    const correctedTargetType = getCorrectTargetType(targetType, zoneNumber);

    switch (correctedTargetType) {
        case Types.TargetTypes.Pace_Zone:
            if (sportTypeKey === Types.SportTypes.Swimming) {
                // pool swim is always per 100m, convert value to meters if pool size is in yards
                const paceDistance = poolLengthUnit === 'yard' ? 100 * ConversionFactors.YardsToMeters : 100;
                const mins = Math.floor(Math.round(paceDistance / targetValueOne) / 60);
                const secs = Math.floor(Math.round(paceDistance / targetValueOne) % 60);
                targetValueOne = `${mins}:${secs < 10 ? '0' + secs : secs}`;
            } else {
                targetValueOne = customPersonalizePace(targetValueOne, isSecondaryTarget);
            }

            targetValueTwo = customPersonalizePace(targetValueTwo, isSecondaryTarget);
            break;
        case Types.TargetTypes.Speed_Zone:
            targetValueOne = Personalizer.personalizeSpeed(targetValueOne, 'cycling', null);
            targetValueTwo = Personalizer.personalizeSpeed(targetValueTwo, 'cycling', null);
            break;
    }

    const retVal = {
        targetType: correctedTargetType,
        targetValueOne: targetValueOne,
        targetValueTwo: targetValueTwo,
        zoneNumber: zoneNumber,
        powerCurveDuration: powerCurveDuration,
        powerCurveScale: powerCurveScale
    };

    if (powerUnit) {
        retVal.powerUnit = POWER_UNIT.PERCENT_FTP;
    }

    return retVal;
};

const getTargetObject = (
    valueOne,
    valueTwo,
    zoneNumber,
    powerCurveDuration,
    powerCurveScale,
    selectedTargetType,
    isSecondaryTarget
) => {
    let retVal;
    if (isSecondaryTarget) {
        retVal = {
            secondaryTargetType: {
                workoutTargetTypeKey: selectedTargetType
            },
            secondaryTargetValueOne: valueOne,
            secondaryTargetValueTwo: valueTwo,
            secondaryZoneNumber: zoneNumber
        };
        if (selectedTargetType === Types.TargetTypes.Power_Curve) {
            retVal.secondaryTargetPowerCurveRange = 10;
            retVal.secondaryTargetPowerCurveDuration = powerCurveDuration;
            retVal.secondaryTargetPowerCurveScale = powerCurveScale;
        }
    } else {
        retVal = {
            targetType: {
                workoutTargetTypeKey: selectedTargetType
            },
            targetValueOne: valueOne,
            targetValueTwo: valueTwo,
            zoneNumber: zoneNumber
        };

        if (selectedTargetType === Types.TargetTypes.Power_Curve) {
            retVal.powerCurveRange = 10;
            retVal.powerCurveDuration = powerCurveDuration;
            retVal.powerCurveScale = powerCurveScale;
        }
    }

    return retVal;
};

/**
 * Description: Get the default value for selected target type
 * Backbone Reference: customPersonalizePace() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @param {*} value
 * @param {*} isSecondaryTarget
 * @returns {number|string}
 */
export const customPersonalizePace = (value, isSecondaryTarget) => {
    // Can't use Personalizer.personalizePace because anything that has an hour or more returns 0
    const speed = Personalizer.convertSpeed(value);

    if (speed === 0.0) {
        return 0;
    }

    let seconds = Math.round(3600 / speed),
        hours = Math.floor(seconds / 3600),
        minutes = Math.floor((seconds - hours * 3600) / 60);

    seconds = Math.floor(seconds - hours * 3600 - minutes * 60);

    return (
        minutes +
        hours * 60 +
        ':' +
        formatNumber(seconds, isSecondaryTarget ? NumberFormats.TWO_DIGITS : NumberFormats.NO_DECIMAL_PAD_2_ZEROS)
    );
};

/**
 * Description: Get the default value for selected target type
 * Backbone Reference: convertPaceToSpeed() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param mmss
 */
export const convertPaceToSpeed = (mmss) => {
    const timeArray = mmss.split(':');
    let totalSeconds = 0;
    if (timeArray.length === 2) {
        let minutes = parseInt(timeArray[0]);
        let seconds = parseInt(timeArray[1]);

        if (seconds >= 60) {
            minutes++;
            seconds -= 60;
        }

        totalSeconds = minutes * 60 + seconds;
    } else if (timeArray.length === 1) {
        // assume minutes
        totalSeconds = parseInt(timeArray[0]) * 60;
    }
    return Personalizer.convertPaceToMetersPerSecond(totalSeconds);
};

export const isMetric = () => {
    return Personalizer.getMeasurementSystem() === Personalizer.MeasurementSystems.METRIC;
};

//Select_Heart_Rate_Zone/Select_Power_Zone will be saved as Heart_Rate_Zone/Power_Zone
//We should map this kind of record back to Select_Heart_Rate_Zone/Select_Power_Zone
export const getCorrectTargetType = (typeKey, zoneNumber) => {
    if (typeKey === Types.TargetTypes.Heart_Rate_Zone) {
        return zoneNumber && !isNaN(zoneNumber) && zoneNumber > -1
            ? Types.ChildTargetTypes.Select_Heart_Rate_Zone
            : typeKey;
    } else if (typeKey === Types.TargetTypes.Power_Zone) {
        return zoneNumber && !isNaN(zoneNumber) && zoneNumber > -1 ? Types.ChildTargetTypes.Select_Power_Zone : typeKey;
    } else {
        return typeKey;
    }
};

/**
 * Description: calculate Watt value from given Ftp
 * Backbone Reference: calculateAndSetWattFromFtp() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param valueFTP
 * @param powerZones
 */
export const calculateWattFromFTP = (valueFTP, powerZones) => {
    if (
        powerZones.functionalThresholdPower &&
        !isNaN(powerZones.functionalThresholdPower) &&
        valueFTP &&
        !isNaN(valueFTP)
    ) {
        const parsedValue = parseInt(valueFTP, 10);
        const parsedCalculateWattFromFtp = parseInt(powerZones.functionalThresholdPower, 10);
        return Math.floor((parsedCalculateWattFromFtp / 100) * parsedValue);
    }
};

/**
 * Description: calculate Watt value to Ftp
 * Backbone Reference: wattsToFTP() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param valueWatts
 * @param powerZones
 */
export const calculateWattsToFTP = (valueWatts, powerZones) => {
    const ftp = parseInt(powerZones.functionalThresholdPower, 10);
    return Math.floor((valueWatts / ftp) * 100);
};

/**
 * Description: get the validator config of target type
 * Backbone Reference: validatorSettings() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {{from: {validationFunc: ((function(*, *): *)|*), reporterMessage: (function(*): *), config: {maxValue: number, required: boolean}}, to: {validationFunc: ((function(*, *): *)|*), reporterMessage: (function(*): *), config: {maxValue: number, required: boolean}}}}
 * @param targetType
 * @param powerZones
 * @param sportType
 * @param isFTP
 * @param fromValue
 * @param toValue
 */
export const getTargetValidatorSettings = (targetType, powerZones, sportType, isFTP, fromValue, toValue) => {
    const numberParser = new NumberParser({
        numberSeparator: Personalizer.getNumberSeparator()
    });
    switch (targetType) {
        case Types.TargetTypes.Pace_Zone:
            return {
                from: {
                    validationFunc: DurationTimeValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: {
                            parse: (value) => Personalizer.parseTime(value)
                        },
                        maxValue: 100000,
                        required: true
                    }
                },
                to: {
                    validationFunc: DurationTimeValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: {
                            parse: (value) => Personalizer.parseTime(value)
                        },
                        maxValue: 100000,
                        required: true
                    }
                }
            };
        case Types.TargetTypes.Speed_Zone:
            return {
                from: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: numberParser,
                        minValue: 1,
                        maxValue: 9999,
                        required: true
                    }
                },
                to: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: numberParser,
                        minValue: 1,
                        maxValue: 9999,
                        required: true
                    }
                }
            };
        case Types.TargetTypes.Cadence:
            return {
                from: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: numberParser,
                        minValue: 1,
                        maxValue: 9999,
                        required: true
                    }
                },
                to: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: numberParser,
                        minValue: 1,
                        maxValue: 9999,
                        required: true
                    }
                }
            };
        case Types.TargetTypes.Power_Zone:
            const powerZoneBySport = getZoneBySportType(powerZones, sportType);
            return {
                from: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: (input) =>
                        powerZoneValidationReporterMessage(
                            false,
                            fromValue,
                            toValue,
                            sportType,
                            powerZoneBySport,
                            isFTP
                        ),
                    config: {
                        parser: numberParser,
                        powerZone: powerZoneBySport,
                        isFTP: isFTP,
                        minValue: 1,
                        maxValue: getPowerZoneMaxValue(sportType, isFTP, powerZoneBySport),
                        required: true
                    }
                },
                to: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: (input) =>
                        powerZoneValidationReporterMessage(
                            true,
                            fromValue,
                            toValue,
                            sportType,
                            powerZoneBySport,
                            isFTP
                        ),
                    config: {
                        parser: numberParser,
                        powerZone: powerZoneBySport,
                        minValue: 1,
                        maxValue: getPowerZoneMaxValue(sportType, isFTP, powerZoneBySport),
                        required: true
                    }
                }
            };
        case Types.TargetTypes.Heart_Rate_Zone:
            return {
                from: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: numberParser,
                        minValue: 1,
                        maxValue: 999,
                        required: true
                    }
                },
                to: {
                    validationFunc: NumberValidator.validateInput,
                    reporterMessage: validationReporterMessage,
                    config: {
                        parser: numberParser,
                        minValue: 1,
                        maxValue: 999,
                        required: true
                    }
                }
            };
        case Types.TargetTypes.Power_Curve:
            return {
                validationFunc: NumberValidator.validateInput,
                reporterMessage: validationReporterMessage,
                config: {
                    parser: numberParser,
                    minValue: 1,
                    maxValue: 100,
                    required: true
                }
            };
    }
};

/**
 * Description: the error message for target validation
 * Backbone Reference: validationReporterMessage() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param value
 */
const validationReporterMessage = (value) => {
    return i18next.t('validation:validation.activity.distance.invalid', { 0: value });
};

/**
 * Description: the error message for power zone
 * Backbone Reference: powerZoneValidationReporterMessage() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param isMaxField
 * @param fromValue
 * @param toValue
 * @param sportType
 * @param isFTP
 * @param powerZones
 */
const powerZoneValidationReporterMessage = (isMaxField, fromValue, toValue, sportType, powerZones, isFTP) => {
    let minPower = 1;
    let maxPower = powerZonesMax.default;

    switch (sportType) {
        case Types.SportTypes.Running:
            maxPower = powerZonesMax.running;
            break;
        case Types.SportTypes.Cycling:
            maxPower = powerZonesMax.cycling;
            break;
    }

    if (
        +fromValue >= +toValue ||
        +fromValue === 0 ||
        +toValue > (isFTP ? calculateWattsToFTP(maxPower, powerZones) : maxPower)
    ) {
        minPower = isMaxField ? +fromValue + 1 : minPower;
        if (isMaxField) {
            maxPower = isFTP ? calculateWattsToFTP(maxPower, powerZones) : maxPower;
        } else {
            maxPower = +toValue - 1;
        }
    }

    // handle edge case
    if (+fromValue === maxPower - 1 || (!isMaxField && +toValue === 2) || maxPower < 1) {
        return i18next.t('validation:validation.activity.distance.invalid', { 0: isMaxField ? +toValue : +fromValue });
    }

    return i18next.t('validation:workout_power_validation_range', {
        0: minPower,
        1: maxPower
    });
};

/**
 * Description: get max power zone value
 * Backbone Reference: getPowerZoneMaxValue() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param sportType
 * @param isFTP
 * @param powerZones
 */
const getPowerZoneMaxValue = (sportType, isFTP, powerZones) => {
    let value = powerZonesMax.default;

    if (sportType === Types.SportTypes.Running) {
        value = powerZonesMax.running;
    } else if (sportType === Types.SportTypes.Cycling) {
        value = validateCyclingPowerZone(isFTP, powerZones);
    }

    return value;
};

/**
 * Description: get max power of cycling
 * Backbone Reference: validateCyclingPowerZone() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param isFTP
 * @param powerZones
 */
const validateCyclingPowerZone = (isFTP, powerZones) => {
    return isFTP ? getMaxFTP(powerZones) : powerZonesMax.cycling;
};

/**
 * Description: get max ftp of cycling
 * Backbone Reference: getMaxFTP() in backbone/pages/workouts/dropdowns/Targets.js and backbone/pages/workouts/dropdowns/SecondaryTargets.js
 *
 * @returns {number|string}
 * @param powerZones
 */
const getMaxFTP = (powerZones) => {
    const parsedCalculateWattFromFtp = parseInt(powerZones.functionalThresholdPower, 10);
    return Math.floor(powerZonesMax.cycling / (parsedCalculateWattFromFtp / 100));
};

export const getPlaceholder = (targetType) => {
    switch (targetType) {
        case Types.TargetTypes.Speed_Zone:
        case Types.TargetTypes.Cadence:
            return '00';
        case Types.TargetTypes.Pace_Zone:
            return '00:00';
        case Types.TargetTypes.Heart_Rate_Zone:
        case Types.TargetTypes.Power_Zone:
        case Types.TargetTypes.Power_Curve:
            return '000';
    }
};

export const getTargetInputFormatter = (targetType) => {
    switch (targetType) {
        case Types.TargetTypes.Speed_Zone:
            return (input) => formatNumberInputSeparator(input, 1);
        case Types.TargetTypes.Pace_Zone:
            return (input) => input.replace(/([^0-9:])/g, '').replace(/::/g, ':');
        case Types.TargetTypes.Heart_Rate_Zone:
        case Types.TargetTypes.Power_Zone:
        case Types.TargetTypes.Cadence:
        case Types.TargetTypes.Power_Curve:
            return (input) => input.replace(/([^0-9])/g, '');
    }
};

export const getInputMaxLength = (targetType) => {
    switch (targetType) {
        case Types.TargetTypes.Speed_Zone:
            return 6;
        case Types.TargetTypes.Cadence:
        case Types.TargetTypes.Power_Zone:
            return 4;
        case Types.TargetTypes.Pace_Zone:
            return 9;
        case Types.TargetTypes.Heart_Rate_Zone:
        case Types.TargetTypes.Power_Curve:
            return 3;
    }
};

export const getPowerCurveWorkoutTargetDates = () => {
    dayjs.extend(utc);
    const today = dayjs()
        .utc()
        .local()
        .format('YYYY-MM-DD');
    const ninetyDaysAgo = dayjs()
        .utc()
        .local()
        .subtract(90, 'days')
        .format('YYYY-MM-DD');
    return { startDate: ninetyDaysAgo, endDate: today };
};

export const getDurationStepTime = (workoutStepData) => {
    return isDurationStepTimeSelected(workoutStepData) ? workoutStepData['endConditionValue'] : 1200; //default duration time is set to 20 mins
};

export const isDurationStepTimeSelected = (workoutStepData) => {
    return workoutStepData.endCondition.conditionTypeKey === Types.ConditionTypes.Time;
};

/**
 * Description: get the closest power curve interval
 * Backbone Reference: closestValue() in backbone/pages/workouts/util/StepUtil.js
 *
 * @returns number
 * @param num second
 */
export const closestDurationValue = (num) => {
    let mid;
    let lo = 0;
    let hi = POSSIBLE_POWER_CURVE_DURATIONS.length - 1;

    while (hi - lo > 1) {
        mid = Math.floor((lo + hi) / 2);
        if (POSSIBLE_POWER_CURVE_DURATIONS[mid] < num) {
            lo = mid;
        } else {
            hi = mid;
        }
    }

    if (num - POSSIBLE_POWER_CURVE_DURATIONS[lo] <= POSSIBLE_POWER_CURVE_DURATIONS[hi] - num) {
        return POSSIBLE_POWER_CURVE_DURATIONS[lo];
    }

    return POSSIBLE_POWER_CURVE_DURATIONS[hi];
};
